import React, { type ComponentType } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';

import { getCustomerConfig, getOrganizationConfig } from '@hopdrive/sdk-configs';
import { useRooftopProductFacts } from '@hopdrive/feature-portal-products';
import {
  resolveCapabilities,
  type MovePlannerHostValue,
  type PlannerConnectorAdapter,
  type PlannerLane,
  type PlannerLanesPort,
  type PlannerLocation,
  type PlannerNotifyOptions,
  type PlannerRooftop,
  type PlannerModalActionProps,
  type PlannerSession,
  type PlannerTransport,
} from '@hopdrive/feature-move-planner';

import siteConfig from '@config';
import { useUser, useRooftop, setRooftop, useCustomerConfig, setCustomerConfig, setOrganizationConfig } from '@store';
import { checkForPowerRole, getClaimsFromToken, getTokenInfo } from '@features/auth/services/authService';
import { fetchData } from '@services/connectorService';
import { useAddressSuggestions } from '@hooks/useAddressSuggestions';
import { useConnectorAutocomplete } from '@hooks/useConnectorAutocomplete';
import { ConnectorAutocomplete } from '@components/ConnectorAutocomplete';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/ModalComponents';
import { dispatchLiteMove, refetchMoveWithRelationships } from '@features/moves/services/moveDispatchService';
import { useGetMoveForDispatchLazyQuery } from '@features/moves/queries/getMoveForDispatch.query.generated';
import { useUpdateMoveTimesMutation } from '@features/moves/queries/updateMoveTimes.mutation.generated';
import { useGetLaneByCustomerAndLocationsLazyQuery } from '@features/schedule/queries/getLaneByCustomerAndLocations.query.generated';
import { lanesReadyForRequest } from '@hopdrive/feature-lane-lifecycle';
import { useLaneEnrichment } from '@features/moveCreation/hooks/useLaneEnrichment';
import { laneNeedsEnrichment, mergeLaneEstimates } from '@features/moveCreation/utils/laneEnrichment';
import { mergeConnectorData } from '@features/moveCreation/services/connectorUtils';
import { useUpsertMovePlannerLocationsMutation } from '@features/moveCreation/queries/upsertMovePlannerLocations.mutation.generated';
import type { Lanes } from '@gql/base-types';

import { EMBED_CAPABILITIES } from './embedCapabilities';
import { plannerEmbedSettings, plannerPrefill } from './plannerEmbedInputs';

const ROOFTOP_KEY = 'rooftopId';

const lanes: PlannerLanesPort = {
  useLaneEnrichment: (lane, generation, onUpdate) =>
    useLaneEnrichment(lane as Lanes | null | undefined, generation, onUpdate as (updated: Lanes) => void),
  laneNeedsEnrichment: lane => laneNeedsEnrichment(lane as Lanes | null | undefined),
  mergeLaneEstimates: (existing, enriched) =>
    mergeLaneEstimates(existing as unknown as Lanes, enriched as unknown as Lanes) as unknown as typeof existing,
  lanesReadyForRequest: (mainLane, inverseLane, requiresInverseLane) =>
    lanesReadyForRequest(mainLane as never, inverseLane as never, requiresInverseLane),
};

const notify = (kind: 'success' | 'error' | 'warning' | 'info') => (message: string, options: PlannerNotifyOptions) =>
  toast[kind](message, options);

/** The Embed's answers to everything the Move Planner needs from its host. */
export function useEmbedPlannerHost(): MovePlannerHostValue {
  const user = useUser();
  const customerConfig = useCustomerConfig();
  const [searchParams] = useSearchParams();
  const [getLane] = useGetLaneByCustomerAndLocationsLazyQuery();
  const [getMoveForDispatch] = useGetMoveForDispatchLazyQuery();
  const [updateMoveTimes] = useUpdateMoveTimesMutation();
  const [upsertLocations] = useUpsertMovePlannerLocationsMutation();

  const settings = React.useMemo(() => plannerEmbedSettings(searchParams), [searchParams]);
  const prefill = React.useMemo(() => plannerPrefill(searchParams, settings.initialType), [searchParams, settings.initialType]);

  const transport = React.useMemo<PlannerTransport>(
    () => ({
      checkBusinessHours: async ({ lane, readyBy, isMovePair }) => {
        const userToken = await user?.getToken();
        const response = await fetch('/.netlify/functions/checkBusinessHours', {
          method: 'POST',
          body: JSON.stringify({ lane, ready_by: readyBy, isMovePair }),
          headers: { authorization: `Bearer ${userToken}` },
        });
        return response.json();
      },
      buildLaneAndInverse: async ({ customerId, pickup, delivery }) => {
        const userToken = await user?.getToken();
        const res = await axios({
          method: `POST`,
          url: `/.netlify/functions/buildLaneAndInverse`,
          data: { lane: { customerId, pickup, delivery } },
          headers: { authorization: `Bearer ${userToken}` },
        });
        return res?.data?.success ? (res.data.upsertableLanes as PlannerLane[]) : null;
      },
      findLanePair: async ({ customerId, pickupId, deliveryId }) => {
        const [forward, inverse] = await Promise.all([
          getLane({ variables: { customerId, pickupId, deliveryId } }),
          getLane({ variables: { customerId, pickupId: deliveryId, deliveryId: pickupId } }),
        ]);
        return {
          forward: (forward.data?.lanes?.[0] as PlannerLane | undefined) ?? null,
          inverse: (inverse.data?.lanes?.[0] as PlannerLane | undefined) ?? null,
        };
      },
      verifyMobilePhone: async phone => {
        const userToken = await user?.getToken();
        const res = await axios({
          method: `POST`,
          url: `/.netlify/functions/verifyMobilePhone`,
          data: { phone },
          headers: { authorization: `Bearer ${userToken}` },
        });
        return res?.data?.success && (res?.data?.isMobile === true || res?.data?.isMobile === false) ? res.data.isMobile : null;
      },
      writeAdminNotification: async variables => {
        const userToken = await user?.getToken();
        await axios({
          method: `POST`,
          url: `/.netlify/functions/writeAdminNotification`,
          data: { variables },
          headers: { authorization: `Bearer ${userToken}` },
        });
      },
      useAddressSuggestions: () => useAddressSuggestions(),
      dispatch: {
        refetchMove: async moveId => (await refetchMoveWithRelationships(moveId, getMoveForDispatch)) as { id: number } | null,
        dispatchToDriver: async ({ move, driverId, time }) => {
          await dispatchLiteMove(move as unknown as Parameters<typeof dispatchLiteMove>[0], driverId, time, 'assign', user || null, updateMoveTimes, window.location.origin);
        },
      },
    }),
    [getLane, getMoveForDispatch, updateMoveTimes, user]
  );

  const connector = React.useMemo<PlannerConnectorAdapter>(
    () => ({
      Field: ConnectorAutocomplete,
      useTriggerEnabled: triggerType => useConnectorAutocomplete({ triggerType }).enabled,
      fetch: (triggerType, lookupValue) => fetchData(triggerType, lookupValue),
      overwriteExisting: triggerType =>
        customerConfig?.connector_triggers?.fetchTriggers?.[triggerType]?.overwriteExisting ?? false,
      merge: (record, consumer, vehicle, overwriteExisting, excludeFields) =>
        mergeConnectorData(record as never, consumer, vehicle, overwriteExisting, excludeFields) as {
          consumerUpdates: Record<string, string>;
          vehicleUpdates: Record<string, string>;
        },
      resolveCustomerLocation: async (record, rooftopId) => {
        if (!record.customerAddressLine1 && !record.customerAddressCity) return null;
        const fullAddress = [
          record.customerAddressLine1,
          record.customerAddressLine2,
          record.customerAddressCity,
          record.customerAddressState,
          record.customerAddressZip,
        ]
          .filter(Boolean)
          .join(', ');
        if (!fullAddress) return null;

        try {
          const userToken = await user?.getToken();
          if (!userToken) {
            console.warn('[Connector] No auth token available for location building');
            return null;
          }
          const upsertableLocation = {
            active: 1,
            address: fullAddress,
            address_line_one: record.customerAddressLine1 || null,
            address_line_two: record.customerAddressLine2 || null,
            city: record.customerAddressCity || null,
            state: record.customerAddressState || null,
            zip_code: record.customerAddressZip || null,
            customer_id: rooftopId,
            favorite: false,
            type: 'customer',
            name: fullAddress,
            createdat: 'now()',
            updatedat: 'now()',
          };
          const buildRes = await axios({
            method: 'POST',
            url: '/.netlify/functions/buildLocationFromAddress',
            data: { location: upsertableLocation },
            headers: { authorization: `Bearer ${userToken}` },
          });
          if (!buildRes?.data?.success) {
            console.warn('[Connector] Failed to build location from address:', buildRes?.data?.error);
            return null;
          }
          const upsertRes = await upsertLocations({ variables: { upsertableLocations: [buildRes.data.upsertableLocation] } });
          const upserted = upsertRes?.data?.insert_locations?.returning?.[0];
          if (!upserted) {
            console.warn('[Connector] Failed to upsert location');
            return null;
          }
          return upserted as PlannerLocation;
        } catch (error) {
          console.error('[Connector] Error building location from address:', error);
          return null;
        }
      },
    }),
    [customerConfig, upsertLocations, user]
  );

  // The session is its own value, stable while the user is, so the planner's per-session loads do not rerun
  // when another part of the host changes.
  const session = React.useMemo<PlannerSession>(
    () => ({
      customerId: user?.claims?.['x-hasura-customer-id'] ?? undefined,
      actorEmail: user?.claims?.['x-hasura-user-email'] || null,
      userId: user?.claims?.['x-hasura-user-id'] ?? null,
      allowedPayerIds: user?.claims?.['x-hasura-allowed-payers'] || [],
      isPowerRole: checkForPowerRole(user),
      readClaims: async () => {
        if (!user) return null;
        const userToken = await user.getToken();
        if (!userToken) throw new Error(`User token not found`);
        const claims = await getClaimsFromToken();
        if (!claims) throw new Error(`User claims not found`);
        return { role: claims['x-hasura-default-role'] };
      },
      getToken: async () => (await user?.getToken()) ?? undefined,
      tokenInfo: getTokenInfo,
      reportIdentity: {
        role: user?.claims?.['x-hasura-default-role'],
        customerId: user?.claims?.['x-hasura-customer-id'],
      },
      // The embed grants every feature its hash session reaches.
      useFeature: () => true,
    
    }),
    [user]
  );

  return React.useMemo<MovePlannerHostValue>(
    () => ({
      session,
      rooftop: {
        useWorkingRooftop: () => useRooftop() as PlannerRooftop | null | undefined,
        setWorkingRooftop: rooftop => setRooftop(rooftop as never),
        useCustomerConfig: () => useCustomerConfig(),
        setCustomerConfig: config => setCustomerConfig(config),
        // The embed plans with the merged config it fetches by customer, not the rooftop row's
        readPlannerConfig: rooftop => getCustomerConfig({ customerId: rooftop.id }),
        changeWorkingRooftop: async rooftop => {
          localStorage?.setItem(ROOFTOP_KEY, JSON.stringify(rooftop.id));
          setRooftop(rooftop as never);
          const config = await getCustomerConfig({ customerId: rooftop.id });
          if (config) setCustomerConfig(config);
          const organizationId = (rooftop as { organization?: { id?: number } }).organization?.id;
          if (organizationId) {
            const organizationConfig = await getOrganizationConfig({ organizationId });
            if (organizationConfig) setOrganizationConfig(organizationConfig);
          }
        },
        // A hash session opens on its first rooftop.
        restoreRooftopId: () => undefined,
        rememberRooftopId: id => localStorage?.setItem(ROOFTOP_KEY, JSON.stringify(id)),
      },
      products: { useRooftopFacts: rooftopId => useRooftopProductFacts(rooftopId) },
      lanes,
      transport,
      report: { captureException: (error, context) => Sentry.captureException(error, context as never) },
      notify: { success: notify('success'), error: notify('error'), warning: notify('warning'), info: notify('info') },
      config: { adminUrl: siteConfig.hopdrive.admin.url },
      // The embed's ModalAction types its colors without `utility`, which its theme has and its Button
      // renders; the planner asks for it only in the overlap warning, which the embed does not show.
      components: { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction: ModalAction as ComponentType<PlannerModalActionProps> },
      capabilities: resolveCapabilities(EMBED_CAPABILITIES),
      embed: { settings, prefill, connector },
    }),
    [connector, prefill, settings, transport, session, user]
  );
}
